import type { Notification } from "../../types/notifications";
import type { ClientDetailTab } from "../clients/ClientDetails";
import { RELEASE_NOTE_EVENT, isSafeHttpsUrl } from "../../utils/releaseNotes";

/**
 * Notifications that deep-link to a client's profile (via body.client_id)
 * rather than to a calendar/transaction target, each opening the tab where
 * the new record shows. Gift cards live in the client's "credits" tab (held
 * cards for a claim, purchased cards for a purchase); packages open the
 * "packages" tab.
 */
const CLIENT_TAB_BY_EVENT: Record<string, ClientDetailTab> = {
  package_purchased: "packages",
  gift_card_purchased: "credits",
  gift_card_claimed: "credits",
};

export interface NotificationTarget {
  /** Accessible label for the row's open action. */
  label:
    | "Go to client profile"
    | "Navigate to transaction"
    | "Open release notes";
  /** An in-app route, or an absolute https URL when `external`. */
  path: string;
  state?: Record<string, unknown>;
  /** Open `path` in a new tab (no opener) instead of routing in the app. */
  external?: boolean;
}

/**
 * Where an alert opens, or null when it has nothing to open (e.g. a gift card
 * bought by an unlinked purchaser, or a release note without a link). Release
 * notes open their https link in a new tab. Client-scoped events go to the client's
 * profile tab; a stray client_id on any other event never hijacks the
 * transaction link.
 */
export function getNotificationTarget(
  notification: Pick<Notification, "event_name" | "body">,
): NotificationTarget | null {
  const { event_name: eventName, body } = notification;
  // Release notes link out to the full notes, never into the app.
  if (eventName === RELEASE_NOTE_EVENT) {
    const link = body?.link;
    return isSafeHttpsUrl(link)
      ? { label: "Open release notes", path: link, external: true }
      : null;
  }
  const clientTab = CLIENT_TAB_BY_EVENT[eventName];
  if (clientTab && body?.client_id) {
    return {
      label: "Go to client profile",
      path: `/admin/clients/${body.client_id}`,
      state: { targetTab: clientTab },
    };
  }
  if (body?.target_obj === "transaction" && body?.target_id) {
    return {
      label: "Navigate to transaction",
      path: "/admin/calendar",
      state: {
        targetTransactionId: body.target_id,
        targetTab:
          eventName === "post_appointment_form.submitted" ? "forms" : undefined,
      },
    };
  }
  return null;
}
